import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const navLinks = [
    { labelKey: "nav.home", path: "/" },
    { labelKey: "nav.services", path: "/services" },
    { labelKey: "nav.about", path: "/about" },
    { labelKey: "nav.contact", path: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? "bg-white shadow-sm border-b border-gray-100"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer">
          <span
            className={`text-2xl font-extrabold tracking-tight transition-colors duration-300 ${
              scrolled || !isHome ? "text-emerald-700" : "text-white"
            }`}
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "-0.01em" }}
          >
            Medi<span className={`${scrolled || !isHome ? "text-gray-800" : "text-emerald-300"}`}>Green</span>
          </span>
          <span
            className={`ml-2 text-xs font-semibold tracking-widest uppercase transition-colors duration-300 ${
              scrolled || !isHome ? "text-gray-400" : "text-white/60"
            }`}
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            LLC
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium tracking-wide transition-colors duration-200 whitespace-nowrap relative group ${
                scrolled || !isHome ? "text-gray-700 hover:text-emerald-700" : "text-white hover:text-emerald-300"
              } ${location.pathname === link.path ? (scrolled || !isHome ? "text-emerald-700" : "text-emerald-300") : ""}`}
            >
              {t(link.labelKey)}
              <span
                className={`absolute -bottom-1 left-0 h-0.5 bg-emerald-600 transition-all duration-200 ${
                  location.pathname === link.path ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </Link>
          ))}
        </nav>

        {/* Right: Language + CTA */}
        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher light={!scrolled && isHome} />
          <Link
            to="/contact"
            className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            {t("nav.cta")}
            <i className="ri-arrow-right-line text-base" />
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden text-2xl cursor-pointer ${scrolled || !isHome ? "text-gray-800" : "text-white"}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium text-gray-700 hover:text-emerald-700 transition-colors cursor-pointer ${
                location.pathname === link.path ? "text-emerald-700" : ""
              }`}
            >
              {t(link.labelKey)}
            </Link>
          ))}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <LanguageSwitcher />
            <Link
              to="/contact"
              className="bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 rounded-md text-center whitespace-nowrap cursor-pointer"
            >
              {t("nav.cta")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
